import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { hardwareContent } from '@/mocks/home';

function useInView<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.unobserve(el); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

export default function HardwareSection() {
  const { ref: sectionRef, inView } = useInView<HTMLDivElement>();

  return (
    <section className="relative bg-background-50 py-16 md:py-24 overflow-hidden" ref={sectionRef}>
      <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
        {/* Header */}
        <div className={`flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <span className="text-xs md:text-sm font-semibold text-foreground-400 uppercase tracking-widest font-label">
              {hardwareContent.titleEn}
            </span>
            <h2 className="mt-1 text-2xl md:text-3xl lg:text-4xl font-black text-foreground-50">
              {hardwareContent.subtitle}
            </h2>
          </div>
          <Link
            to={hardwareContent.ctaHref}
            className="inline-flex items-center gap-2 px-6 py-2.5 border border-foreground-600 text-foreground-300 text-sm font-medium rounded-md hover:bg-foreground-50 hover:text-background-50 hover:border-foreground-50 transition-all whitespace-nowrap self-start md:self-auto"
          >
            {hardwareContent.cta}
          </Link>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {hardwareContent.items.map((item, index) => (
            <Link
              key={item.id}
              to={item.href}
              className={`group relative bg-background-100 rounded-lg overflow-hidden border border-background-200/50 hover:border-primary-500/40 transition-all duration-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${150 + index * 100}ms` }}
            >
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden bg-background-200">
                <img
                  src={item.image}
                  alt={item.model}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Info */}
              <div className="p-4 md:p-5">
                <p className="text-sm text-foreground-200 font-medium whitespace-pre-line leading-snug group-hover:text-primary-400 transition-colors">
                  {item.title}
                </p>
                <span className="mt-2 inline-block text-xs font-semibold text-primary-400 bg-primary-950/30 px-3 py-1 rounded-full">
                  {item.model}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}